import { Card, CardContent } from "@/components/ui/card";

export const APP_STORE_URL = "https://apps.apple.com/us/app/edufocal-academy/id6478810839";

const APPLE_PATH = "M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z";

export function AppleIcon({ className = "w-6 h-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`${className} flex-shrink-0 fill-current`} aria-hidden="true">
      <path d={APPLE_PATH} />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" fill="none" aria-hidden="true">
      <path d="M3 3L3 12L11 12Z" fill="#4285F4" />
      <path d="M3 12L3 21L11 12Z" fill="#34A853" />
      <path d="M3 3L11 12L21 12Z" fill="#EA4335" />
      <path d="M11 12L3 21L21 12Z" fill="#FBBC04" />
    </svg>
  );
}

type Tone = "dark" | "light";
const box = (tone: Tone) =>
  tone === "dark"
    ? "bg-black/60 border-white/10"
    : "bg-white/10 border-white/20";

/** Live link to the iOS app. */
export function AppStoreBadge({ tone = "dark", className = "" }: { tone?: Tone; className?: string }) {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download EduFocal Academy on the App Store"
      data-testid="link-app-store"
      className={`flex items-center gap-3 rounded-xl px-4 py-2.5 border shadow-lg min-w-[148px] text-white transition-all hover:-translate-y-0.5 hover:shadow-xl hover:border-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${box(tone)} ${className}`}
    >
      <AppleIcon />
      <div className="text-left">
        <p className="text-white/60 text-[9px] leading-none font-medium">Download on the</p>
        <p className="text-white text-sm font-bold leading-tight font-[Montserrat]">App Store</p>
      </div>
    </a>
  );
}

/** Android is not out yet; keeps the "soon" pill. */
export function GooglePlayBadge({ tone = "dark" }: { tone?: Tone }) {
  return (
    <div className="relative">
      <div className={`flex items-center gap-3 rounded-xl px-4 py-2.5 border shadow-lg min-w-[148px] opacity-70 cursor-not-allowed select-none ${box(tone)}`} aria-label="Google Play, coming soon">
        <PlayIcon />
        <div className="text-left">
          <p className="text-white/60 text-[9px] leading-none font-medium">Get it on</p>
          <p className="text-white text-sm font-bold leading-tight font-[Montserrat]">Google Play</p>
        </div>
      </div>
      <span className="absolute -top-2 -right-2 bg-[#FFC209] text-gray-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none uppercase tracking-wide">Soon</span>
    </div>
  );
}

/** Small card for signed-in dashboards. */
export function GetTheAppCard({ audience = "student" }: { audience?: "student" | "parent" }) {
  const title = audience === "parent" ? "EduFocal Academy is on the App Store" : "Owlbert is on the App Store!";
  const body = audience === "parent"
    ? "Quizzes, lessons, streaks and the report card on iPhone and iPad, with reminders that keep the habit going. Android is coming."
    : "Quizzes, lessons, duels and your streak on iPhone or iPad. Ask a grown-up to install it. Android is coming soon.";
  return (
    <Card className="rounded-xl border-[#043b55] bg-[#043b55] text-white shadow-sm" data-testid="card-get-the-app">
      <CardContent className="p-4 md:p-5 flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
          <AppleIcon className="w-5 h-5" />
        </div>
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-semibold leading-tight">{title}</p>
          <p className="text-xs text-white/70 leading-relaxed">{body}</p>
          <AppStoreBadge tone="dark" className="inline-flex min-w-0" />
        </div>
      </CardContent>
    </Card>
  );
}
